import { ProductSubTier } from '@sparemin/api-sdk';
import { SegmentedControl } from '@sparemin/blockhead';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ProIndicator from 'containers/ProIndicator';
import { pushModal } from 'redux/modules/modal';

import { hasFullEpisodeVideoAccessSelector } from 'redux/modules/pricing';
import { ClipSelectionMode } from './types';
import { block } from './utils';

interface ClipVideoTypeSelectorProps {
  isDisabled?: boolean;
  onChange: (value: ClipSelectionMode) => void;
  value: ClipSelectionMode;
}

const NOT_ALLOWED_SUBTIERS = [
  ProductSubTier.FREE,
  ProductSubTier.EDUCATION_NONPROFIT,
  ProductSubTier.BASIC_20200922_PAID,
  ProductSubTier.BASIC_20211112_PAID,
  ProductSubTier.BASIC_20250516_PAID,
  ProductSubTier.BASIC_20250818_PAID,
  ProductSubTier.BASIC_EDUCATION_CHARITY,
];

const ClipVideoTypeSelector: React.FunctionComponent<ClipVideoTypeSelectorProps> = props => {
  const { isDisabled, onChange, value } = props;

  const hasFullEpisodeVideoAccess = useSelector(
    hasFullEpisodeVideoAccessSelector,
  );

  const dispatch = useDispatch();

  const handleUpgrade = React.useCallback((): void => {
    dispatch(pushModal({ name: 'FullEpisodeCaptionsUpsell' }));
  }, [dispatch]);

  const handleChange = React.useCallback(
    () => (nextValue: ClipSelectionMode) => {
      if (!hasFullEpisodeVideoAccess && nextValue === 'full-episode') {
        handleUpgrade();
      } else {
        onChange(nextValue);
      }
    },
    [handleUpgrade, hasFullEpisodeVideoAccess, onChange],
  );

  return (
    <div className={block('segment-picker-wrapper')}>
      <ProIndicator blacklist={NOT_ALLOWED_SUBTIERS}>
        {({ badge }) => {
          const FullEpisodeLabel = !hasFullEpisodeVideoAccess ? (
            <>
              Use full episode{' '}
              <span className={block('pro-badge')}>{badge}</span>
            </>
          ) : (
            'Use full episode'
          );

          return (
            <SegmentedControl
              className={block('segment-picker')}
              isDisabled={isDisabled}
              optionClassName={block('segment-picker-option')}
              onChange={handleChange()}
              options={[
                {
                  label: 'Select a clip',
                  value: 'clip',
                },
                {
                  label: FullEpisodeLabel,
                  value: 'full-episode',
                },
              ]}
              value={value}
            />
          );
        }}
      </ProIndicator>
    </div>
  );
};

export default ClipVideoTypeSelector;
